import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Link as LinkIcon, Quotes, Spinner } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const Block = ({ block, referenceIndex }) => {
  if (block.type === 'paragraph') {
    return (
      <p className="text-[17px] leading-[1.75] text-foreground/90 whitespace-pre-line">{block.text}</p>
    );
  }

  if (block.type === 'highlight') {
    return (
      <p className="text-xl md:text-2xl font-heading font-medium leading-snug tracking-tight border-l-2 border-primary pl-6 py-1 text-foreground">
        {block.text}
      </p>
    );
  }

  if (block.type === 'quote') {
    return (
      <blockquote className="rounded-3xl bg-white border border-black/[0.05] p-7 md:p-9">
        <Quotes size={26} weight="fill" className="text-primary/30 mb-3" />
        <p className="text-lg md:text-xl leading-relaxed">{block.text}</p>
        {block.attribution && (
          <footer className="mt-4 text-sm text-foreground-muted">— {block.attribution}</footer>
        )}
      </blockquote>
    );
  }

  if (block.type === 'image') {
    return (
      <figure>
        <div className="rounded-3xl overflow-hidden bg-slate-100">
          <img src={block.url} alt={block.caption || ''} className="w-full object-cover" />
        </div>
        {block.caption && (
          <figcaption className="mt-3 text-xs text-foreground-muted">{block.caption}</figcaption>
        )}
      </figure>
    );
  }

  if (block.type === 'reference') {
    return (
      <a
        href={block.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-start gap-3 rounded-2xl border border-black/[0.06] bg-white px-5 py-4 hover:border-primary/30 transition-colors"
      >
        <span className="mt-0.5 shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
          {referenceIndex}
        </span>
        <span>
          <span className="block text-sm font-medium group-hover:text-primary transition-colors">
            {block.label || block.url}
          </span>
          {block.text && <span className="block text-xs text-foreground-muted mt-1">{block.text}</span>}
        </span>
        <LinkIcon size={15} className="ml-auto mt-1 text-foreground-muted shrink-0" />
      </a>
    );
  }

  return null;
};

const PostDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/posts/${slug}`);
        setPost(res.data);
      } catch (error) {
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const date = post?.published_at || post?.created_at;
  const body = (post?.blocks || []).filter((b) => b.type !== 'reference');
  const references = (post?.blocks || []).filter((b) => b.type === 'reference');

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="post-detail-page">
      <Header />

      <div className="mx-auto w-full max-w-[760px] px-6 md:px-10 pt-5 pb-20">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 py-3 text-sm text-foreground-muted hover:text-foreground transition-colors"
          data-testid="back-to-news"
        >
          <ArrowLeft size={16} /> Back to News &amp; Stories
        </Link>

        {loading ? (
          <div className="py-24 flex justify-center" data-testid="post-loading">
            <Spinner size={28} className="animate-spin text-primary" />
          </div>
        ) : !post ? (
          <div className="py-24 text-center" data-testid="post-not-found">
            <h1 className="text-2xl font-heading font-semibold">This story is not available.</h1>
            <p className="text-sm text-foreground-muted mt-2">
              It may have been unpublished or the link is wrong.
            </p>
            <Button asChild className="mt-6 rounded-full h-11 px-6 bg-primary hover:bg-primary-hover text-white">
              <Link to="/news">See all stories</Link>
            </Button>
          </div>
        ) : (
          <article>
            <div className="flex items-center gap-3 mt-2 mb-5">
              <span
                className="px-3 h-[26px] inline-flex items-center rounded-full text-[12px] font-medium bg-primary/10 text-primary"
                data-testid="post-category"
              >
                {post.category_label || post.category}
              </span>
              {date && (
                <span className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
                  <Calendar size={13} />
                  {new Date(date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>

            <h1
              className="text-3xl md:text-4xl lg:text-[44px] font-heading font-semibold tracking-tight leading-[1.1]"
              data-testid="post-title"
            >
              {post.title}
            </h1>

            <p className="mt-5 text-lg text-foreground-muted leading-relaxed">{post.excerpt}</p>

            {post.cover_image && (
              <div className="mt-8 rounded-3xl overflow-hidden bg-slate-100">
                <img src={post.cover_image} alt={post.title} className="w-full object-cover" />
              </div>
            )}

            <div className="mt-10 space-y-7" data-testid="post-body">
              {body.map((block, index) => (
                <Block key={index} block={block} />
              ))}
            </div>

            {references.length > 0 && (
              <section className="mt-14 pt-8 border-t border-black/[0.08]" data-testid="post-references">
                <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-muted mb-4">
                  References
                </p>
                <div className="space-y-3">
                  {references.map((block, index) => (
                    <Block key={index} block={block} referenceIndex={index + 1} />
                  ))}
                </div>
              </section>
            )}

            <div className="mt-14 p-7 md:p-9 rounded-3xl bg-slate-900 text-white">
              <h2 className="text-xl md:text-2xl font-heading font-medium mb-2">
                Want to own a piece of the next one?
              </h2>
              <p className="text-white/60 text-sm md:text-base mb-6">
                Browse the projects currently open to investors.
              </p>
              <Button asChild className="rounded-full h-11 px-6 bg-primary hover:bg-primary-hover text-white">
                <Link to="/projects" data-testid="post-cta-projects">
                  See open projects
                </Link>
              </Button>
            </div>
          </article>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PostDetailPage;
